import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import useAsyncEffect from 'use-async-effect';
import moment from 'moment-timezone';
import { FiGlobe, FiMessageCircle, FiArrowRight } from 'react-icons/fi';
import { CommunicationsAnnouncementStatisticsResult } from '../../../model/CommunicationsAnnouncementsResult';
import api from '../../../api/api';
import { to, pluralize } from '../../../util/util';
import { initials } from '../Header';

interface Props {
    stockId: string;
    sendGuid: string;
}

const TZ = 'Australia/Sydney';
const when = (utc: string) => moment(utc).tz(TZ).format('D MMM, h:mm a');
const shareholder = (shares: number) => `Shareholder · ${shares.toLocaleString()} shares`;

// spec 2:58395. Omitted: likes and Who Liked (no source), Export Statistics / Export List, Broker / Media roles.
const AnnouncementStatistics: React.FC<Props> = ({ stockId, sendGuid }) => {
    const history = useHistory();
    const [stats, setStats] = useState<CommunicationsAnnouncementStatisticsResult | null>(null);
    const [error, setError] = useState('');

    useAsyncEffect(async isMounted => {
        const [err, data] = await to(api().getCommunicationsAnnouncementStatistics(stockId, sendGuid));
        if (!isMounted()) return;
        if (err) setError(err.message || 'Failed to load the statistics');
        else setStats(data);
    }, [stockId, sendGuid]);

    if (error) return <div className="main"><p className="error">{error}</p></div>;
    if (!stats) return <div className="main"><p className="loading">Loading statistics…</p></div>;

    const tile = (value: string, label: string, sub: string) =>
        <div className="tile"><div className="v">{value}</div><div className="l">{label}</div><div className="s">{sub}</div></div>;
    const opened = stats.emailsSent ? ` (${Math.round(100 * (stats.emailsOpened || 0) / stats.emailsSent)}%)` : '';

    return <div className="main announcement-statistics">
        <div className="tiles">
            {tile(stats.views.toLocaleString(), 'views', `${stats.signedInViews.toLocaleString()} signed in · ${stats.notSignedInViews.toLocaleString()} not signed in`)}
            {tile(stats.questions.length.toLocaleString(), 'questions', 'asked on your website')}
            {stats.emailsSent != null
                ? tile(stats.emailsSent.toLocaleString(), 'emails sent', `${(stats.emailsOpened || 0).toLocaleString()} opened${opened}`)
                : tile('—', 'emails sent', 'not sent yet')}
        </div>

        <div className="card">
            <h2>Who Viewed</h2>
            <p className="sub">{pluralize('view', stats.views)} · {pluralize('signed-in investor', stats.viewers.length)} named</p>
            <ul className="people">
                {stats.viewers.map((v, i) => <li key={i}>
                    <span className="mark">{initials(v.name)}</span>
                    <div className="who">
                        <div className="name">{v.name}</div>
                        <div className="detail">{v.shares ? shareholder(v.shares) : `Signed up ${moment(v.signedUp).tz(TZ).format('D MMM')} · not on the register`}</div>
                    </div>
                    <span className="right">{pluralize('view', v.views)}</span>
                </li>)}
                {stats.notSignedInViews > 0 && <li>
                    <span className="mark muted"><FiGlobe /></span>
                    <div className="who">
                        <div className="name muted">Not signed in</div>
                        <div className="detail">Counted but not named — no account</div>
                    </div>
                    <span className="right">{pluralize('view', stats.notSignedInViews)}</span>
                </li>}
            </ul>
            {stats.views === 0 && <p className="empty">No views yet.</p>}
        </div>

        <div className="card">
            <h2>Who Asked a Question</h2>
            <p className="sub">{stats.questions.length
                ? `${pluralize('question', stats.questions.length)} about this announcement. Answers are written in one place, so everyone sees the same reply.`
                : 'No questions about this announcement yet.'}</p>
            <ul className="people questions">
                {stats.questions.map((q, i) => <li key={i}>
                    {q.shares ? <span className="mark">{initials(q.name || '')}</span> : <span className="mark muted"><FiGlobe /></span>}
                    <div className="who">
                        <div><span className="name">{q.name}</span> <span className="detail">{q.shares ? shareholder(q.shares) : 'Not on the register'}</span></div>
                        <div className="quote">“{q.comment}”</div>
                    </div>
                    <span className="right">{when(q.created)}</span>
                </li>)}
            </ul>
            <div className="callout">
                <FiMessageCircle />
                <span>Answers are written in one place — Communications › Investor questions — so nothing is answered twice and every shareholder sees the same reply.</span>
                <button type="button" className="btn-primary" onClick={() => history.push("/communications/investor-questions")}><FiArrowRight /> Answer Questions</button>
            </div>
        </div>
    </div>;
};

export default AnnouncementStatistics;
